import { useState } from "react";
// ui
import { Button, Typography, Modal, Box, Alert, IconButton, Tooltip } from "@mui/material";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

// jotai
import { useAtomValue } from "jotai";
import { AppAtoms } from "../../lib/store";
import { useWindowSize } from "../../lib/useWindowSize";

interface ResetPasswordModalProps {
  onModal: boolean;
  swCloseModal: () => void;
  resetPasswordInf: {
    status: null | "loading" | "open" | "success" | "failed";
    key: { [key: string]: string };
    message: string;
  };
  setResetPasswordInf: (value: {
    status: null | "loading" | "open" | "success" | "failed";
    key: { [key: string]: string };
    message: string;
  }) => void;
  resetPassword: () => void;
}

export function ResetPasswordModal(props: ResetPasswordModalProps) {
  const {
    onModal,
    swCloseModal,
    resetPasswordInf,
    setResetPasswordInf,
    resetPassword,
  } = props;

  const [showPassword, setShowPassword] = useState(false);
  const { windowWidth, windowHeight } = useWindowSize();

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: windowWidth > 568 ? "500px" : "calc(100% - 82px)",
    maxHeight: "70vh",
    bgcolor: "background.paper",
    border: "2px solid #000",
    boxShadow: 24,
    p: 4,
  };
  return (
    <Modal
      open={onModal}
      onClose={(_event, reason) => {
        if (reason === "backdropClick") return;
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style} data-testid="reset-password-modal">
        <Box
          style={{
            fontSize: "18px",
            color: "rgb(50 50 50)",
          }}
          key="deleteTitleBox"
          className="shippori-antique-regular"
        >
          パスワードリセット
        </Box>
        <Typography id="modal-modal-description" sx={{ mt: 2, mb: 5 }}>
          {resetPasswordInf.status === "open" ? (
            <>
              <Alert severity="error">
                初期パスワードに強制的に変更します。
              </Alert>
              <Box style={{ display: windowWidth > 385 ? "flex" : undefined }}>
                <Box
                  style={{
                    marginTop: "10px",
                    height: "40px",
                    width:
                      windowWidth > 385
                        ? "calc(50% - 5px)"
                        : "calc(100% - 10px)",
                    marginLeft: windowWidth > 385 ? undefined : "5px",
                    textTransform: "none",
                  }}
                >
                  <Button
                    style={{
                      height: "40px",
                      width: "100%",
                    }}
                    variant="contained"
                    onClick={resetPassword}
                    data-testid="reset-password-execute-button"
                  >
                    <Box className="m-plus-rounded-1c-regular">
                      パスワードリセット
                    </Box>
                    <VpnKeyIcon
                      sx={{ marginLeft: "6px", color: "white" }}
                      fontSize="medium"
                    />
                  </Button>
                </Box>
                <Box
                  style={{
                    marginTop: "10px",
                    height: "40px",
                    width:
                      windowWidth > 385
                        ? "calc(50% - 5px)"
                        : "calc(100% - 10px)",
                    marginLeft: windowWidth > 385 ? "10px" : "5px",
                    textTransform: "none",
                  }}
                >
                  <Button
                    variant="contained"
                    style={{
                      height: "40px",
                      width: "100%",
                    }}
                    onClick={swCloseModal}
                    data-testid="reset-password-close-button"
                  >
                    <Box className="m-plus-rounded-1c-regular">閉じる</Box>
                  </Button>
                </Box>
              </Box>
            </>
          ) : resetPasswordInf.status === "success" ? (
            <>
              <Box className="shippori-antique-regular" sx={{ mb: 2 }}>
                パスワードのリセットに成功しました。
              </Box>
              {[
                { label: "アカウント名", value: resetPasswordInf.key.account_name || "", masked: false },
                { label: "メールアドレス", value: resetPasswordInf.key.email || "", masked: false },
                { label: "初期パスワード", value: resetPasswordInf.key.temp_password || "", masked: true },
              ].map((item) => (
                <Box key={item.label} sx={{ display: "flex", alignItems: "center", mb: 1.5, py: 0.5 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {item.label}
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: "monospace" }}>
                      {item.masked && !showPassword ? "●●●●●●●●" : item.value}
                    </Typography>
                  </Box>
                  {item.masked && (
                    <Tooltip title={showPassword ? "パスワードを隠す" : "パスワードを表示"}>
                      <IconButton onClick={() => setShowPassword((prev) => !prev)} size="small" sx={{ ml: 0.5 }}>
                        {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              ))}
              <Button
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                fullWidth
                sx={{ mt: 1, textTransform: "none" }}
                data-testid="reset-password-copy-all"
                onClick={() => {
                  const text = `アカウント名: ${resetPasswordInf.key.account_name || ""}\nメールアドレス: ${resetPasswordInf.key.email || ""}\n初期パスワード: ${resetPasswordInf.key.temp_password || ""}`;
                  navigator.clipboard.writeText(text);
                }}
              >
                すべてコピー
              </Button>
              <Box>
                <Button
                  style={{
                    marginTop: "15px",
                    height: "40px",
                    width: "100%",
                    textTransform: "none",
                  }}
                  variant="contained"
                  onClick={swCloseModal}
                  data-testid="reset-password-success-close-button"
                >
                  <Box className="m-plus-rounded-1c-regular">閉じる</Box>
                </Button>
              </Box>
            </>
          ) : resetPasswordInf.status === "failed" ? (
            <>
              <Box className="shippori-antique-regular">
                {resetPasswordInf.message}
              </Box>
              <Box>
                <Button
                  style={{
                    marginTop: "30px",
                    marginLeft: windowWidth > 385 ? "15%" : "5px",
                    marginBottom: "-25px",
                    height: "40px",
                    width: windowWidth > 385 ? "70%" : "calc(100% - 10px)",
                    textTransform: "none",
                  }}
                  variant="contained"
                  data-testid="reset-password-retry-button"
                  onClick={() =>
                    setResetPasswordInf({
                      ...resetPasswordInf,
                      status: "open",
                      message: "",
                    })
                  }
                >
                  <Box className="m-plus-rounded-1c-regular">戻る</Box>
                </Button>
              </Box>
            </>
          ) : null}
        </Typography>
      </Box>
    </Modal>
  );
}
